/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationsContext';
import { formatStoredDate } from '../../utils/datetime';

const severityConfig = {
  CRITICAL: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30', icon: '🔴' },
  ERROR: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30', icon: '❌' },
  WARNING: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30', icon: '⚠️' },
  NOTICE: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30', icon: '💡' },
  INFO: { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/30', icon: 'ℹ️' },
};

const categoryIcons = {
  TRADE_EXECUTED: '📈',
  TRADE_CLOSED: '📉',
  BACKTEST: '🧪',
  ACCOUNT: '👤',
  BOT_RUN: '🤖',
  BOT_SIGNAL: '📡',
  INDICATOR_ALERT: '📊',
  RISK: '⚠️',
  SYSTEM: '⚙️',
  BILLING: '💳',
};

const getSeverityConfig = (sev) => severityConfig[sev] || severityConfig.INFO;

export default function NotificationsDrawer({ open, onClose }) {
  const ctx = useNotifications();
  const { items, markRead, markAll, filters, setFilters, loading, loadingMore, hasMore, fetchList, fetchMore } = ctx || {
    items: [],
    markRead: async () => {},
    markAll: async () => {},
    filters: {},
    setFilters: () => {},
    loading: false,
    loadingMore: false,
    hasMore: false,
    fetchList: async () => {},
    fetchMore: async () => {},
  };
  const [selected, setSelected] = useState([]);
  const navigate = useNavigate();

  // Intersection observer sentinel for infinite scroll
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchMore();
        }
      },
      { root: null, rootMargin: '200px', threshold: 0 }
    );
    observerRef.current.observe(sentinel);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [open, hasMore, loadingMore, loading, fetchMore]);

  const handleNotificationClick = (n) => {
    // Determine URL from action_url field, or fallback from data context
    const url = n.action_url || n.data?.action_url;
    if (!url) return;
    // Mark as read first
    if (!n.read_at) markRead([n.id]);
    onClose();
    navigate(url);
  };

  // Refresh when opened or filters change
  useEffect(() => {
    if (!open) return;
    const params = filters?.unread ? { unread: 1, category: filters?.category } : { category: filters?.category };
    fetchList(params);
  }, [open, filters?.unread, filters?.category, fetchList]);

  const filtered = useMemo(() => {
    let list = items || [];
    if (filters?.category) list = list.filter(n => n.category === filters.category);
    if (filters?.unread) list = list.filter(n => !n.read_at);
    return list;
  }, [items, filters]);

  const unreadCount = useMemo(() => (items || []).filter(n => !n.read_at).length, [items]);

  const toggleSelected = (id) => setSelected((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const markSelected = async () => {
    if (selected.length === 0) return;
    await markRead(selected);
    setSelected([]);
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatStoredDate(dateStr, undefined, '-');
  };

  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-[70] flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      {/* Drawer */}
      <div className="w-full max-w-lg h-full bg-backgroundBody border-l border-borderDefault shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="p-4 bg-backgroundSurface border-b border-borderDefault">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <span className="text-xl">🔔</span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-textPrimary">Notifications</h2>
                <p className="text-xs text-textSecondary">
                  {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
                </p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-backgroundBody hover:bg-slate-700 flex items-center justify-center transition-colors"
            >
              <span className="text-textSecondary">✕</span>
            </button>
          </div>
          
          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-backgroundBody cursor-pointer hover:bg-slate-700 transition-colors">
              <input
                type="checkbox"
                checked={!!filters?.unread}
                onChange={(e) => setFilters((f) => ({ ...f, unread: e.target.checked ? 1 : undefined }))}
                className="w-4 h-4 rounded accent-primary"
              />
              <span className="text-sm text-textSecondary">Unread only</span>
            </label>
            
            <select
              className="text-sm px-3 py-1.5 rounded-lg bg-backgroundBody border border-borderDefault text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary/50"
              value={filters?.category || ''}
              onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value || undefined }))}
            >
              <option value="">All categories</option>
              {Object.keys(categoryIcons).map(c => (
                <option key={c} value={c}>{categoryIcons[c]} {c.replace(/_/g, ' ')}</option>
              ))}
            </select>
            
            <button 
              className="ml-auto px-3 py-1.5 text-sm rounded-lg bg-primary hover:bg-primaryHover text-textAccent font-medium transition-colors"
              onClick={markAll}
            >
              Mark all read
            </button>
          </div>
        </div>

        {/* Notification List */}
        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
          )}
          
          {filtered.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-700/50 flex items-center justify-center mb-4">
                <span className="text-3xl">📭</span>
              </div>
              <p className="text-textSecondary">No notifications</p>
              <p className="text-sm text-textSecondary/70">You're all caught up!</p>
            </div>
          )}
          
          <div className="divide-y divide-borderDefault">
            {filtered.map((n) => {
              const config = getSeverityConfig(n.severity);
              const isUnread = !n.read_at;
              
              return (
                <div 
                  key={n.id} 
                  className={`p-4 hover:bg-slate-800/50 transition-colors ${isUnread ? 'bg-primary/5' : ''} ${(n.action_url || n.data?.action_url) ? 'cursor-pointer' : ''}`}
                  onClick={() => handleNotificationClick(n)}
                >
                  <div className="flex gap-3">
                    {/* Checkbox */}
                    <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                      <input 
                        type="checkbox" 
                        checked={selected.includes(n.id)} 
                        onChange={() => toggleSelected(n.id)}
                        className="w-4 h-4 rounded accent-primary cursor-pointer"
                      />
                    </div>
                    
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-xl ${config.bg} flex items-center justify-center flex-shrink-0`}>
                      <span className="text-lg">{categoryIcons[n.category] || '📌'}</span>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className={`font-medium ${isUnread ? 'text-textPrimary' : 'text-textSecondary'}`}>
                          {n.title}
                        </h3>
                        <span className="text-xs text-textSecondary whitespace-nowrap">
                          {formatTimeAgo(n.created_at)}
                        </span>
                      </div>
                      
                      {n.body && (
                        <p className="text-sm text-textSecondary line-clamp-2 mb-2">
                          {n.body}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${config.bg} ${config.text}`}>
                          {n.severity || 'INFO'}
                        </span>
                        <span className="px-2 py-0.5 rounded-md text-xs bg-slate-700/50 text-textSecondary">
                          {(n.category || 'GENERAL').replace(/_/g, ' ')}
                        </span>
                        {isUnread && (
                          <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                        )}
                        
                        {isUnread && (
                          <button 
                            className="ml-auto text-xs text-primary hover:text-primaryHover transition-colors"
                            onClick={(e) => { e.stopPropagation(); markRead([n.id]); }}
                          >
                            Mark read
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="h-1" />

            {/* Loading more indicator */}
            {loadingMore && (
              <div className="flex items-center justify-center p-4">
                <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full"></div>
                <span className="ml-2 text-sm text-textSecondary">Loading more...</span>
              </div>
            )}

            {/* End of list */}
            {!hasMore && filtered.length > 0 && !loading && (
              <div className="text-center text-xs text-textSecondary/50 py-3">
                No more notifications
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        {selected.length > 0 && (
          <div className="p-3 bg-backgroundSurface border-t border-borderDefault">
            <button 
              className="w-full py-2 text-sm rounded-lg bg-primary hover:bg-primaryHover text-textAccent font-medium transition-colors"
              onClick={markSelected}
            >
              Mark {selected.length} selected as read
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
